import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminExtendGift, adminGetGift, adminGiftAction, isOk, messageOf } from "../../../services/gift";
import { statusLabel } from "./index";
import "./admin-gifts.css";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// Which actions make sense for a gift in each status (the backend enforces the same rules).
const ACTIONS = {
  suspend: { label: "Suspend", needsReason: true, allowed: [ "active", "payment_pending" ] },
  disable: { label: "Disable", needsReason: true, allowed: [ "active", "payment_pending", "suspended" ] },
  reactivate: { label: "Reactivate", needsReason: true, allowed: [ "suspended", "disabled" ] },
  expire: { label: "Expire now", needsReason: true, allowed: [ "active", "payment_pending", "suspended", "awaiting_purchaser" ] },
  "resend-receipt": { label: "Resend receipt", needsReason: false, allowed: [ "active", "payment_pending", "redeemed", "suspended", "disabled" ] },
  "block-purchaser": { label: "Block purchaser", needsReason: true, allowed: null },
};

const WARNINGS = {
  suspend: "Suspending also cancels any booking waiting on the purchaser's payment (the medium sees a normal client cancellation).",
  disable: "Disabling also cancels any booking waiting on the purchaser's payment (the medium sees a normal client cancellation).",
  expire: "Expiring ends the gift now (its code stops working) and also cancels any booking waiting on the purchaser's payment. You can extend it later.",
  "block-purchaser": "Blocks this purchaser's account, email and creation IP from creating new gifts.",
};

// Admin: one gift — summary, bookings, event timeline and moderation actions.
export default function AdminGiftDetail() {
  const { id } = useParams();
  const [gift, setGift] = useState(null);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDays, setExtendDays] = useState("30");
  const [extendReason, setExtendReason] = useState("");

  const load = () => {
    adminGetGift(id).then((res) => {
      if (isOk(res)) {
        setGift(res.data.data);
      } else {
        toast.error(messageOf(res, "Gift not found."), { position: "top-center" });
      }
    });
  };

  useEffect(load, [id]);

  const run = async (action) => {
    const meta = ACTIONS[action];
    if (meta.needsReason && !reason.trim()) {
      toast.error("Please give a reason.", { position: "top-center" });
      return;
    }
    setBusy(true);
    const res = await adminGiftAction(id, action, meta.needsReason ? reason.trim() : null);
    setBusy(false);
    if (isOk(res)) {
      toast.success(messageOf(res, "Done."), { position: "top-center" });
      setPending(null);
      setReason("");
      load();
    } else {
      toast.error(messageOf(res, "That action could not be completed."), { position: "top-center" });
    }
  };

  const extend = async () => {
    const days = parseInt(extendDays, 10);
    if (!days || days < 1) {
      toast.error("Enter the number of days to add.", { position: "top-center" });
      return;
    }
    if (!extendReason.trim()) {
      toast.error("Please give a reason.", { position: "top-center" });
      return;
    }
    setBusy(true);
    const res = await adminExtendGift(id, { days, reason: extendReason.trim() });
    setBusy(false);
    if (isOk(res)) {
      toast.success(messageOf(res, "Expiry updated."), { position: "top-center" });
      setExtendOpen(false);
      setExtendReason("");
      load();
    } else {
      toast.error(messageOf(res, "The expiry could not be changed."), { position: "top-center" });
    }
  };

  if (!gift) {
    return <div className="admin-gifts">Loading…</div>;
  }

  const canExtend = !!gift.code_last4 && [ "active", "payment_pending", "suspended", "awaiting_purchaser", "expired" ].includes(gift.status);

  const available = Object.keys(ACTIONS).filter((key) => {
    const allowed = ACTIONS[key].allowed;
    if (key === "block-purchaser") return !gift.purchaser_blocked;
    return allowed.includes(gift.status);
  });

  return (
    <div className="admin-gifts">
      <p>
        <Link to="/gifts">&larr; All gifts</Link>
      </p>

      <div className="admin-gift-card">
        <h2>
          {gift.reference} <span className={`admin-gift-status status-${gift.status}`}>{statusLabel(gift.status)}</span>
        </h2>
        <div className="admin-gift-grid">
          <div><b>Code</b><br />{gift.code_last4 ? `…${gift.code_last4}` : "—"}</div>
          <div><b>Covers up to</b><br />{gift.currency} {gift.max_price}</div>
          <div><b>Shown to recipient</b><br />{gift.show_amount ? `"Worth up to ${gift.currency} ${gift.max_price}"` : "No amount"}</div>
          <div><b>Expires</b><br />{formatDate(gift.expires_at)}</div>
          <div><b>Purchaser</b><br />{gift.purchaser_name || "—"}<br />{gift.purchaser_email}<br />{gift.purchaser_phone || ""}</div>
          <div><b>Contact channel</b><br />{gift.contact_channel}</div>
          <div><b>Fee</b><br />{gift.currency} {gift.fee_amount} &middot; paid {formatDate(gift.fee_paid_at)}<br /><span className="admin-gifts-muted">{gift.fee_payment_intent_id || ""}</span></div>
          <div><b>Disclosure</b><br />v{gift.disclosure_version} &middot; acknowledged {formatDate(gift.fee_acknowledged_at)}</div>
          <div><b>Created</b><br />{formatDate(gift.created_at)} from {gift.created_ip || "—"}</div>
          <div><b>Redeemed</b><br />{formatDate(gift.redeemed_at)}</div>
          <div><b>Purchaser blocked</b><br />{gift.purchaser_blocked ? "Yes" : "No"}</div>
        </div>

        <div className="admin-gift-actions">
          {available.map((key) => (
            <button
              key={key}
              type="button"
              className={`proceed-btn ${pending === key ? "active" : ""}`}
              disabled={busy}
              onClick={() => (ACTIONS[key].needsReason ? setPending(key) : run(key))}
            >
              {ACTIONS[key].label}
            </button>
          ))}
          {canExtend && (
            <button type="button" className={`proceed-btn ${extendOpen ? "active" : ""}`} disabled={busy} onClick={() => setExtendOpen(!extendOpen)}>
              Extend expiry
            </button>
          )}
        </div>

        {extendOpen && (
          <div className="admin-gift-reason">
            <p className="admin-gifts-muted">
              Adds days to the current expiry (or to today if it has already passed). An expired gift becomes usable again.
            </p>
            <input
              type="number"
              className="form-control"
              min={1}
              max={1095}
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              aria-label="Days to add"
            />
            <textarea
              className="form-control mt-2"
              rows={2}
              maxLength={500}
              value={extendReason}
              onChange={(e) => setExtendReason(e.target.value)}
              placeholder="Reason for extending (required, kept in the gift's history)"
            />
            <div className="mt-2">
              <button type="button" className="proceed-btn" disabled={busy} onClick={extend}>
                Confirm extend
              </button>
              <button type="button" className="admin-gifts-link ml-3" onClick={() => setExtendOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {pending && (
          <div className="admin-gift-reason">
            {WARNINGS[pending] && <p className="admin-gifts-muted">{WARNINGS[pending]}</p>}
            <textarea
              className="form-control"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Reason for "${ACTIONS[pending].label}" (required, kept in the gift's history)`}
            />
            <div className="mt-2">
              <button type="button" className="proceed-btn" disabled={busy} onClick={() => run(pending)}>
                Confirm {ACTIONS[pending].label}
              </button>
              <button type="button" className="admin-gifts-link ml-3" onClick={() => setPending(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <h3 className="mt-4">Bookings</h3>
      <div className="table-box-medium admin-appoi admin-upcoming-app">
        <table className="caseTable">
          <tbody>
            <tr className="dark">
              <th>Status</th>
              <th>Recipient email</th>
              <th>Medium</th>
              <th>Appointment</th>
              <th>Price</th>
              <th>Pay by</th>
              <th>Reminder</th>
              <th>Paid / closed</th>
            </tr>
            {gift.redemptions.map((r) => (
              <tr key={r.id} className="innr-dark">
                <td>{r.status}</td>
                <td>{r.recipient_email}</td>
                <td>{r.medium_name || r.medium_id}</td>
                <td>2A{r.appointment_id}X</td>
                <td>
                  {r.currency} {r.price}
                  {r.transaction_id ? <><br /><span className="admin-gifts-muted">txn {r.transaction_id}</span></> : null}
                </td>
                <td>{formatDate(r.payment_deadline_at)}</td>
                <td>{formatDate(r.reminder_sent_at)}</td>
                <td>{formatDate(r.paid_at || r.closed_at)}</td>
              </tr>
            ))}
            {gift.redemptions.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center">Not used yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 className="mt-4">History</h3>
      <div className="table-box-medium admin-appoi admin-upcoming-app">
        <table className="caseTable">
          <tbody>
            <tr className="dark">
              <th>When</th>
              <th>Event</th>
              <th>By</th>
              <th>IP</th>
              <th>Details</th>
            </tr>
            {gift.events.map((event, index) => (
              <tr key={index} className="innr-dark">
                <td>{formatDate(event.created_at)}</td>
                <td>{event.event}</td>
                <td>{event.actor_user_id || "system"}</td>
                <td>{event.ip || "—"}</td>
                <td className="admin-gift-meta">{event.meta ? JSON.stringify(event.meta) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
